/**
 * Absence Tables - DataTables Initialization
 *
 * Handles DataTables for absence data tables:
 * - Basic Search table (server-side processing)
 * - Aggregator tables (client-side processing)
 * - Export functionality
 *
 * @package Absenteeism_WP_Theme
 */

(function($) {
    'use strict';

    // Override DataTables Buttons Bootstrap 5 default button class
    // The Bootstrap 5 extension adds 'btn-primary' by default, but we want 'btn-primary'
    if ($.fn.dataTable && $.fn.dataTable.Buttons) {
        $.extend(true, $.fn.dataTable.Buttons.defaults, {
            dom: {
                button: {
                    className: 'btn'
                }
            }
        });
    }

    /**
     * WCAG 2.1 AA: Add accessible names to DataTables sort buttons.
     * DataTables renders <span class="dt-column-order" role="button" tabindex="0">
     * with no accessible name. This labels each with the column title.
     *
     * @param {HTMLElement} table - The table element from DataTables settings.nTable
     */
    function labelSortButtons(table) {
        $(table).find('.dt-column-order[role="button"]').each(function() {
            var title = $(this).siblings('.dt-column-title').text().trim();
            if (title) {
                $(this).attr('aria-label', 'Sort by ' + title);
            }
        });
    }

    // Store DataTable instances
    var absenceTable = null;
    var buildingTable = null;
    var gradeTable = null;
    var aggregatorTables = [];

    // Module-level filter data for Basic Search (accessible by export functions)
    var filterData = {
        type: 'D',
        year: 2025,
        start_date: '',
        end_date: '',
        counties: [],
        regions: [],
        in_game_network: ''
    };

    // Module-level filter data for Building table
    var buildingFilterData = {
        irn: '',
        start_date: '',
        end_date: '',
        counties: [],
        regions: [],
        in_game_network: ''
    };

    // Module-level filter data for Grade table
    var gradeFilterData = {
        irn: '',
        start_date: '',
        end_date: '',
        counties: [],
        regions: [],
        in_game_network: ''
    };

    /**
     * Initialize on document ready
     */
    $(document).ready(function() {
        //console.log('Absence Tables JS loaded');
        //console.log('jQuery version:', $.fn.jquery);
        //console.log('DataTables available:', typeof $.fn.DataTable !== 'undefined');
        //console.log('absenceTablesData available:', typeof absenceTablesData !== 'undefined');

        // Verify DataTables is loaded
        if (typeof $.fn.DataTable === 'undefined') {
            //console.error('DataTables library not loaded!');
            return;
        }

        // WCAG 2.1 AA: Ensure unique aria-labels on pagination nav elements.
        // DataTables hardcodes aria-label="pagination" on all <nav> elements.
        // A MutationObserver watches the DOM and updates each <nav> as soon as
        // DataTables inserts it, using the closest table's aria-label or id.
        var paginationNavObserver = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                mutation.addedNodes.forEach(function(node) {
                    if (node.nodeType !== 1) {
                        return;
                    }

                    // Check the added node itself and any nav descendants
                    var navElements = [];
                    if (node.tagName === 'NAV' && node.getAttribute('aria-label') === 'pagination') {
                        navElements.push(node);
                    }
                    if (node.querySelectorAll) {
                        var nested = node.querySelectorAll('nav[aria-label="pagination"]');
                        for (var i = 0; i < nested.length; i++) {
                            navElements.push(nested[i]);
                        }
                    }

                    navElements.forEach(function(nav) {
                        var wrapper = nav.closest('.dt-container') || nav.closest('.dataTables_wrapper');
                        if (wrapper) {
                            var table = wrapper.querySelector('table');
                            if (table) {
                                var label = table.getAttribute('aria-label') || table.getAttribute('id') || 'table';
                                nav.setAttribute('aria-label', label + ' pagination');
                            }
                        }
                    });
                });
            });
        });
        paginationNavObserver.observe(document.body, { childList: true, subtree: true });

        // Initialize Basic Search table if it exists
        initBasicSearchTable();

        // Initialize Building table if it exists
        initBuildingTable();

        // Initialize Grade table if it exists
        initGradeTable();

        // Initialize Aggregator tables if they exist
        initAggregatorTables();

        // Add a wrapper to all tables after init
        $('.dataTable').wrap('<div class="table-responsive datatable-styled"></div>');

        // Bind export all button
        bindExportAllButton();
    });

    /**
     * Initialize Basic Search DataTable (server-side processing)
     */
    function initBasicSearchTable() {
        var $table = $('#absence-table');
        var $container = $('#absence-table-container');

        if ($table.length === 0) {
            //console.log('Basic search table not found, skipping initialization');
            return;
        }

        //console.log('Initializing Basic Search DataTable');

        // Check if absenceTablesData is available
        if (typeof absenceTablesData === 'undefined') {
            //console.error('absenceTablesData not defined - DataTables script may not be properly localized');
            return;
        }

        // Update module-level filter data from container data attributes
        filterData.type = $container.data('type') || 'D';
        filterData.year = $container.data('year') || 2025;
        filterData.start_date = $container.data('start-date') || '';
        filterData.end_date = $container.data('end-date') || '';
        filterData.counties = $container.data('counties') || [];
        filterData.regions = $container.data('regions') || [];
        filterData.in_game_network = $container.data('in-game-network') || '';

        //console.log('Filter data:', filterData);
        //console.log('AJAX URL:', absenceTablesData.ajaxurl);
        //console.log('Nonce:', absenceTablesData.nonce ? 'present' : 'missing');

        // Build initial state from URL (page_num, per_page, sort_by, sort_order)
        // so that browser back button and bookmarks restore pagination/sort.
        var districtCols = buildColumnMap(absenceTablesData.columnMaps && absenceTablesData.columnMaps.district);
        var districtInitialState = getTableStateFromUrl('', districtCols.nameToIndex, {
            pageLength: 20,
            sortCol: 0,
            sortDir: 'asc'
        });
        // Suppress URL writes during the initial draw so DataTables' default-order
        // event doesn't clobber the URL before the user interacts.
        var districtSuppressStateUrlUpdate = true;

        try {
            // Initialize DataTable
            absenceTable = $table.DataTable({
                processing: true,
                serverSide: true,
                ajax: {
                    url: absenceTablesData.ajaxurl,
                    type: 'POST',
                    data: function(d) {
                        //console.log('Sending AJAX request with draw:', d.draw);
                        d.action = 'absence_table_data';
                        d.nonce = absenceTablesData.nonce;
                        d.type = filterData.type;
                        d.year = filterData.year;
                        d.start_date = filterData.start_date;
                        d.end_date = filterData.end_date;
                        d.counties = filterData.counties;
                        d.regions = filterData.regions;
                        d.in_game_network = filterData.in_game_network;
                        return d;
                    },
                    dataSrc: function(json) {
                        //console.log('AJAX response received:', json);
                        if (json.error) {
                            //console.error('Server error:', json.error);
                        }
                        return json.data || [];
                    },
                    error: function(xhr, error, thrown) {
                        //console.error('DataTables AJAX error:', error, thrown);
                        //console.error('Response status:', xhr.status);
                        //console.error('Response text:', xhr.responseText);
                    }
                },
                displayStart: districtInitialState.displayStart,
                pageLength: districtInitialState.pageLength,
                lengthMenu: [[10, 20, 50, 100], [10, 20, 50, 100]],
                order: districtInitialState.order,
                columns: [
                    {
                        data: 'name',
                        render: function(data, type, row) {
                            if (type === 'display') {
                                if (row.building_url) {
                                    return '<a class="district-link" href="' + row.building_url + '">' + data + '</a>';
                                }
                            }
                            return data || '';
                        }
                    },
                    { data: 'county', defaultContent: '' },
                    {
                        data: 'enrollment',
                        className: 'text-end',
                        render: function(data, type) {
                            if (data === null || data === undefined) {
                                if (type === 'display') return '<span class="text-muted">-</span>';
                                return -1;
                            }
                            if (type === 'display' && data) {
                                if (data >= 1 && data < 10) {
                                    return '<em>&lt;10</em>';
                                }
                                return Number(data).toLocaleString();
                            }
                            return data || 0;
                        }
                    },
                    {
                        data: 'chronic_pct',
                        className: 'text-end',
                        render: function(data, type) {
                            if (data === null || data === undefined) {
                                if (type === 'display') return '<span class="text-muted">-</span>';
                                return 0;
                            }
                            if (type === 'display') {
                                return '<strong>' + data + '%</strong>';
                            }
                            return data;
                        }
                    },
                    {
                        data: 'change',
                        className: 'text-end',
                        render: renderTrendChange
                    },
                    {
                        data: 'satisfactory',
                        className: 'text-end',
                        render: renderPercentage
                    },
                    {
                        data: 'satisfactory',
                        className: 'text-end',
                        render: renderCount
                    },
                    {
                        data: 'at_risk',
                        className: 'text-end',
                        render: renderPercentage
                    },
                    {
                        data: 'at_risk',
                        className: 'text-end',
                        render: renderCount
                    },
                    {
                        data: 'moderate',
                        className: 'text-end',
                        render: renderPercentage
                    },
                    {
                        data: 'moderate',
                        className: 'text-end',
                        render: renderCount
                    },
                    {
                        data: 'severe',
                        className: 'text-end',
                        render: renderPercentage
                    },
                    {
                        data: 'severe',
                        className: 'text-end',
                        render: renderCount
                    },
                    {
                        data: 'superintendent',
                        orderable: false,
                        defaultContent: ''
                    }
                ],
                dom: '<"row"<"col-6"B><"col-6"f>>rtip',
                buttons: [
                    {
                        text: '<i class="bi bi-filetype-csv"></i> CSV',
                        className: 'btn btn-primary btn-sm',
                        action: function(e, dt, node, config) {
                            var $btn = $(node);
                            var originalText = $btn.html();
                            $btn.prop('disabled', true).html('<i class="bi bi-hourglass-split"></i> Exporting...');

                            exportBasicSearchToCSV().finally(function() {
                                $btn.prop('disabled', false).html(originalText);
                            });
                        }
                    },
                    {
                        text: '<i class="bi bi-file-earmark-spreadsheet"></i> Excel',
                        className: 'btn btn-primary btn-sm',
                        action: function(e, dt, node, config) {
                            var $btn = $(node);
                            var originalText = $btn.html();
                            $btn.prop('disabled', true).html('<i class="bi bi-hourglass-split"></i> Exporting...');

                            exportBasicSearchToExcel().finally(function() {
                                $btn.prop('disabled', false).html(originalText);
                            });
                        }
                    }
                ],
                language: {
                    processing: '<div class="d-flex flex-column align-items-center"><div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div><p class="mt-2 mb-0 text-muted">Loading data...</p></div>',
                    emptyTable: 'No data available for the selected filters.',
                    info: 'Showing _START_ to _END_ of _TOTAL_ districts',
                    infoEmpty: 'Showing 0 to 0 of 0 districts',
                    infoFiltered: '(filtered from _MAX_ total districts)',
                    lengthMenu: 'Show _MENU_ districts',
                    search: 'Search:',
                    zeroRecords: 'No matching districts found'
                },
                drawCallback: function(settings) {
                    initTooltips();
                    initTrendPopovers();
                    labelSortButtons(settings.nTable);
                    // Announce table updates to screen readers
                    if (typeof announceToScreenReader === 'function') {
                        announceToScreenReader('Table updated. Showing ' + settings._iRecordsDisplay + ' districts.');
                    }
                }
            });

            //console.log('Basic Search DataTable initialized successfully');

            // After the initial draw completes, allow subsequent state
            // changes to be persisted to the URL.
            absenceTable.one('init.dt', function() {
                districtSuppressStateUrlUpdate = false;
            });

            // Persist pagination, length, and sort state to the URL on user interaction.
            absenceTable.on('page.dt length.dt order.dt', function() {
                if (districtSuppressStateUrlUpdate) {
                    return;
                }
                updateUrlWithTableState(absenceTable, '', districtCols.indexToName, 20);
            });

            // Edge case: a stale bookmark may request a page past the end
            // of the current dataset. On the first AJAX response, snap back
            // to page 1 if our displayStart exceeds recordsFiltered.
            absenceTable.one('xhr.dt', function(e, settings, json) {
                if (json && typeof json.recordsFiltered === 'number'
                    && districtInitialState.displayStart >= json.recordsFiltered
                    && json.recordsFiltered > 0) {
                    districtSuppressStateUrlUpdate = false;
                    absenceTable.page('first').draw('page');
                }
            });

            // Handle filter form submission
            $('#absence-filter-form').on('submit', function(e) {
                e.preventDefault();

                filterData.start_date = $('#start_date').val() || '';
                filterData.end_date = $('#end_date').val() || '';
                filterData.counties = getCheckedValues('counties[]');
                filterData.regions = getCheckedValues('regions[]');
                filterData.in_game_network = $('#in-game-network').is(':checked') ? '1' : '';

                // Update URL with filter parameters (for chart popovers and bookmarking)
                updateUrlWithFilters(filterData);

                // Reset pagination in URL (but preserve per_page and sort_*).
                var filterUrl = new URL(window.location.href);
                filterUrl.searchParams.delete('page_num');
                window.history.replaceState({}, '', filterUrl.toString());

                // Update filter badge and Clear All Filters button
                updateFilterUI(filterData);

                // Close offcanvas
                var offcanvasEl = document.getElementById('offcanvasFilters');
                if (offcanvasEl) {
                    var offcanvas = bootstrap.Offcanvas.getInstance(offcanvasEl);
                    if (offcanvas) offcanvas.hide();
                }

                // Reload table and reset paging to page 1
                absenceTable.ajax.reload(null, true);

                // Reload chart with updated filters
                reloadChart();
            });

        } catch (e) {
            //console.error('Error initializing Basic Search DataTable:', e);
        }
    }

    /**
     * Initialize Building DataTable (server-side processing)
     */
    function initBuildingTable() {
        var $table = $('#building-table');
        var $container = $('#building-table-container');

        if ($table.length === 0) {
            //console.log('Building table not found, skipping initialization');
            return;
        }

        //console.log('Initializing Building DataTable');

        // Check if absenceTablesData is available
        if (typeof absenceTablesData === 'undefined') {
            //console.error('absenceTablesData not defined - DataTables script may not be properly localized');
            return;
        }

        // Update module-level filter data from container data attributes
        buildingFilterData.irn = $container.data('irn') || '';
        buildingFilterData.start_date = $container.data('start-date') || '';
        buildingFilterData.end_date = $container.data('end-date') || '';
        buildingFilterData.counties = $container.data('counties') || [];
        buildingFilterData.regions = $container.data('regions') || [];
        buildingFilterData.in_game_network = $container.data('in-game-network') || '';

        //console.log('Building filter data:', buildingFilterData);

        // Build initial state from URL (page_num, per_page, sort_by, sort_order)
        // so that browser back button and bookmarks restore pagination/sort.
        var buildingCols = buildColumnMap(absenceTablesData.columnMaps && absenceTablesData.columnMaps.building);
        var buildingInitialState = getTableStateFromUrl('', buildingCols.nameToIndex, {
            pageLength: 20,
            sortCol: 0,
            sortDir: 'asc'
        });
        var buildingSuppressStateUrlUpdate = true;

        try {
            // Initialize DataTable
            buildingTable = $table.DataTable({
                processing: true,
                serverSide: true,
                ajax: {
                    url: absenceTablesData.ajaxurl,
                    type: 'POST',
                    data: function(d) {
                        //console.log('Sending Building AJAX request with draw:', d.draw);
                        d.action = 'building_table_data';
                        d.nonce = absenceTablesData.nonce;
                        d.irn = buildingFilterData.irn;
                        d.start_date = buildingFilterData.start_date;
                        d.end_date = buildingFilterData.end_date;
                        d.counties = buildingFilterData.counties;
                        d.regions = buildingFilterData.regions;
                        d.in_game_network = buildingFilterData.in_game_network;
                        return d;
                    },
                    dataSrc: function(json) {
                        //console.log('Building AJAX response received:', json);
                        if (json.error) {
                            //console.error('Server error:', json.error);
                        }
                        return json.data || [];
                    },
                    error: function(xhr, error, thrown) {
                        //console.error('Building DataTables AJAX error:', error, thrown);
                        //console.error('Response status:', xhr.status);
                        //console.error('Response text:', xhr.responseText);
                    }
                },
                displayStart: buildingInitialState.displayStart,
                pageLength: buildingInitialState.pageLength,
                lengthMenu: [[10, 20, 50, 100], [10, 20, 50, 100]],
                order: buildingInitialState.order,
                columns: [
                    {
                        data: 'name',
                        render: function(data, type, row) {
                            if (type === 'display' && row.grade_url) {
                                return '<a class="district-link" href="' + row.grade_url + '">' + data + '</a>';
                            }
                            return data || '';
                        }
                    },
                    { data: 'county', defaultContent: '' },
                    {
                        data: 'enrollment',
                        className: 'text-end',
                        render: function(data, type) {
                            if (type === 'display' && data) {
                                if (data >= 1 && data < 10) {
                                    return '<em>&lt;10</em>';
                                }
                                return Number(data).toLocaleString();
                            }
                            return data || 0;
                        }
                    },
                    {
                        data: 'chronic_pct',
                        className: 'text-end',
                        render: function(data, type) {
                            if (data === null || data === undefined) {
                                if (type === 'display') return '<span class="text-muted">-</span>';
                                return 0;
                            }
                            if (type === 'display') {
                                return '<strong>' + data + '%</strong>';
                            }
                            return data;
                        }
                    },
                    {
                        data: 'change',
                        className: 'text-end',
                        render: renderTrendChange
                    },
                    {
                        data: 'satisfactory',
                        className: 'text-end',
                        render: renderPercentage
                    },
                    {
                        data: 'satisfactory',
                        className: 'text-end',
                        render: renderCount
                    },
                    {
                        data: 'at_risk',
                        className: 'text-end',
                        render: renderPercentage
                    },
                    {
                        data: 'at_risk',
                        className: 'text-end',
                        render: renderCount
                    },
                    {
                        data: 'moderate',
                        className: 'text-end',
                        render: renderPercentage
                    },
                    {
                        data: 'moderate',
                        className: 'text-end',
                        render: renderCount
                    },
                    {
                        data: 'severe',
                        className: 'text-end',
                        render: renderPercentage
                    },
                    {
                        data: 'severe',
                        className: 'text-end',
                        render: renderCount
                    },
                    {
                        data: 'principal',
                        orderable: false,
                        defaultContent: ''
                    }
                ],
                dom: '<"row"<"col-6"B><"col-6"f>>rtip',
                buttons: [
                    {
                        text: '<i class="bi bi-filetype-csv"></i> CSV',
                        className: 'btn btn-primary btn-sm',
                        action: function(e, dt, node, config) {
                            var $btn = $(node);
                            var originalText = $btn.html();
                            $btn.prop('disabled', true).html('<i class="bi bi-hourglass-split"></i> Exporting...');

                            exportBuildingToCSV().finally(function() {
                                $btn.prop('disabled', false).html(originalText);
                            });
                        }
                    },
                    {
                        text: '<i class="bi bi-file-earmark-spreadsheet"></i> Excel',
                        className: 'btn btn-primary btn-sm',
                        action: function(e, dt, node, config) {
                            var $btn = $(node);
                            var originalText = $btn.html();
                            $btn.prop('disabled', true).html('<i class="bi bi-hourglass-split"></i> Exporting...');

                            exportBuildingToExcel().finally(function() {
                                $btn.prop('disabled', false).html(originalText);
                            });
                        }
                    }
                ],
                language: {
                    processing: '<div class="d-flex flex-column align-items-center"><div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div><p class="mt-2 mb-0 text-muted">Loading data...</p></div>',
                    emptyTable: 'No data available for the selected filters.',
                    info: 'Showing _START_ to _END_ of _TOTAL_ buildings',
                    infoEmpty: 'Showing 0 to 0 of 0 buildings',
                    infoFiltered: '(filtered from _MAX_ total buildings)',
                    lengthMenu: 'Show _MENU_ buildings',
                    search: 'Search:',
                    zeroRecords: 'No matching buildings found'
                },
                drawCallback: function(settings) {
                    initTooltips();
                    initTrendPopovers();
                    labelSortButtons(settings.nTable);
                    // Announce table updates to screen readers
                    if (typeof announceToScreenReader === 'function') {
                        announceToScreenReader('Table updated. Showing ' + settings._iRecordsDisplay + ' buildings.');
                    }
                }
            });

            //console.log('Building DataTable initialized successfully');

            // Persist URL state after initial draw.
            buildingTable.one('init.dt', function() {
                buildingSuppressStateUrlUpdate = false;
            });
            buildingTable.on('page.dt length.dt order.dt', function() {
                if (buildingSuppressStateUrlUpdate) {
                    return;
                }
                updateUrlWithTableState(buildingTable, '', buildingCols.indexToName, 20);
            });
            buildingTable.one('xhr.dt', function(e, settings, json) {
                if (json && typeof json.recordsFiltered === 'number'
                    && buildingInitialState.displayStart >= json.recordsFiltered
                    && json.recordsFiltered > 0) {
                    buildingSuppressStateUrlUpdate = false;
                    buildingTable.page('first').draw('page');
                }
            });

            // Handle filter form submission
            $('#absence-filter-form').on('submit', function(e) {
                e.preventDefault();

                buildingFilterData.start_date = $('#start_date').val() || '';
                buildingFilterData.end_date = $('#end_date').val() || '';
                buildingFilterData.counties = getCheckedValues('counties[]');
                buildingFilterData.regions = getCheckedValues('regions[]');
                buildingFilterData.in_game_network = $('#in-game-network').is(':checked') ? '1' : '';

                // Update URL with filter parameters
                updateUrlWithFilters(buildingFilterData);

                // Reset pagination in URL (but preserve per_page and sort_*).
                var filterUrl = new URL(window.location.href);
                filterUrl.searchParams.delete('page_num');
                window.history.replaceState({}, '', filterUrl.toString());

                // Update filter badge and Clear All Filters button
                updateFilterUI(buildingFilterData);

                // Close offcanvas
                var offcanvasEl = document.getElementById('offcanvasFilters');
                if (offcanvasEl) {
                    var offcanvas = bootstrap.Offcanvas.getInstance(offcanvasEl);
                    if (offcanvas) offcanvas.hide();
                }

                // Reload table and reset paging to page 1
                buildingTable.ajax.reload(null, true);

                // Reload chart with updated filters
                reloadChart();
            });

        } catch (e) {
            //console.error('Error initializing Building DataTable:', e);
        }
    }

    /**
     * Initialize Grade DataTable (server-side processing)
     */
    function initGradeTable() {
        var $table = $('#grade-table');
        var $container = $('#grade-table-container');

        if ($table.length === 0) {
            //console.log('Grade table not found, skipping initialization');
            return;
        }

        //console.log('Initializing Grade DataTable');

        // Check if absenceTablesData is available
        if (typeof absenceTablesData === 'undefined') {
            //console.error('absenceTablesData not defined - DataTables script may not be properly localized');
            return;
        }

        // Update module-level filter data from container data attributes
        gradeFilterData.irn = $container.data('irn') || '';
        gradeFilterData.start_date = $container.data('start-date') || '';
        gradeFilterData.end_date = $container.data('end-date') || '';
        gradeFilterData.counties = $container.data('counties') || [];
        gradeFilterData.regions = $container.data('regions') || [];
        gradeFilterData.in_game_network = $container.data('in-game-network') || '';

        //console.log('Grade filter data:', gradeFilterData);

        // Build initial state from URL (page_num, per_page, sort_by, sort_order).
        // Note: grade table default pageLength is 50, not 20.
        var gradeCols = buildColumnMap(absenceTablesData.columnMaps && absenceTablesData.columnMaps.grade);
        var gradeInitialState = getTableStateFromUrl('', gradeCols.nameToIndex, {
            pageLength: 50,
            sortCol: 0,
            sortDir: 'asc'
        });
        var gradeSuppressStateUrlUpdate = true;

        try {
            // Initialize DataTable
            gradeTable = $table.DataTable({
                processing: true,
                serverSide: true,
                ajax: {
                    url: absenceTablesData.ajaxurl,
                    type: 'POST',
                    data: function(d) {
                        //console.log('Sending Grade AJAX request with draw:', d.draw);
                        d.action = 'grade_table_data';
                        d.nonce = absenceTablesData.nonce;
                        d.irn = gradeFilterData.irn;
                        d.start_date = gradeFilterData.start_date;
                        d.end_date = gradeFilterData.end_date;
                        d.counties = gradeFilterData.counties;
                        d.regions = gradeFilterData.regions;
                        d.in_game_network = gradeFilterData.in_game_network;
                        return d;
                    },
                    dataSrc: function(json) {
                        //console.log('Grade AJAX response received:', json);
                        if (json.error) {
                            //console.error('Server error:', json.error);
                        }
                        return json.data || [];
                    },
                    error: function(xhr, error, thrown) {
                        //console.error('Grade DataTables AJAX error:', error, thrown);
                        //console.error('Response status:', xhr.status);
                        //console.error('Response text:', xhr.responseText);
                    }
                },
                displayStart: gradeInitialState.displayStart,
                pageLength: gradeInitialState.pageLength,
                lengthMenu: [[10, 20, 50, 100], [10, 20, 50, 100]],
                order: gradeInitialState.order,
                columns: [
                    {
                        data: 'grade',
                        className: 'text-uppercase'
                    },
                    {
                        data: 'enrollment',
                        className: 'text-end',
                        render: function(data, type) {
                            if (type === 'display' && data) {
                                if (data >= 1 && data < 10) {
                                    return '<em>&lt;10</em>';
                                }
                                return Number(data).toLocaleString();
                            }
                            return data || 0;
                        }
                    },
                    {
                        data: 'chronic_pct',
                        className: 'text-end',
                        render: function(data, type) {
                            if (data === null || data === undefined) {
                                if (type === 'display') return '<span class="text-muted">-</span>';
                                return 0;
                            }
                            if (type === 'display') {
                                return '<strong>' + data + '%</strong>';
                            }
                            return data;
                        }
                    },
                    {
                        data: 'change',
                        className: 'text-end',
                        render: renderTrendChangeGrade
                    },
                    {
                        data: 'satisfactory',
                        className: 'text-end',
                        render: renderPercentage
                    },
                    {
                        data: 'satisfactory',
                        className: 'text-end',
                        render: renderCount
                    },
                    {
                        data: 'at_risk',
                        className: 'text-end',
                        render: renderPercentage
                    },
                    {
                        data: 'at_risk',
                        className: 'text-end',
                        render: renderCount
                    },
                    {
                        data: 'moderate',
                        className: 'text-end',
                        render: renderPercentage
                    },
                    {
                        data: 'moderate',
                        className: 'text-end',
                        render: renderCount
                    },
                    {
                        data: 'severe',
                        className: 'text-end',
                        render: renderPercentage
                    },
                    {
                        data: 'severe',
                        className: 'text-end',
                        render: renderCount
                    }
                ],
                dom: '<"row"<"col-6"B><"col-6"f>>rtip',
                buttons: [
                    {
                        text: '<i class="bi bi-filetype-csv"></i> CSV',
                        className: 'btn btn-primary btn-sm',
                        action: function(e, dt, node, config) {
                            var $btn = $(node);
                            var originalText = $btn.html();
                            $btn.prop('disabled', true).html('<i class="bi bi-hourglass-split"></i> Exporting...');

                            exportGradeToCSV().finally(function() {
                                $btn.prop('disabled', false).html(originalText);
                            });
                        }
                    },
                    {
                        text: '<i class="bi bi-file-earmark-spreadsheet"></i> Excel',
                        className: 'btn btn-primary btn-sm',
                        action: function(e, dt, node, config) {
                            var $btn = $(node);
                            var originalText = $btn.html();
                            $btn.prop('disabled', true).html('<i class="bi bi-hourglass-split"></i> Exporting...');

                            exportGradeToExcel().finally(function() {
                                $btn.prop('disabled', false).html(originalText);
                            });
                        }
                    }
                ],
                language: {
                    processing: '<div class="d-flex flex-column align-items-center"><div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div><p class="mt-2 mb-0 text-muted">Loading data...</p></div>',
                    emptyTable: 'No data available for the selected filters.',
                    info: 'Showing _START_ to _END_ of _TOTAL_ grades',
                    infoEmpty: 'Showing 0 to 0 of 0 grades',
                    infoFiltered: '(filtered from _MAX_ total grades)',
                    lengthMenu: 'Show _MENU_ grades',
                    search: 'Search:',
                    zeroRecords: 'No matching grades found'
                },
                drawCallback: function(settings) {
                    initTooltips();
                    initTrendPopovers();
                    labelSortButtons(settings.nTable);
                    // Announce table updates to screen readers
                    if (typeof announceToScreenReader === 'function') {
                        announceToScreenReader('Table updated. Showing ' + settings._iRecordsDisplay + ' grades.');
                    }
                }
            });

            //console.log('Grade DataTable initialized successfully');

            // Persist URL state after initial draw.
            gradeTable.one('init.dt', function() {
                gradeSuppressStateUrlUpdate = false;
            });
            gradeTable.on('page.dt length.dt order.dt', function() {
                if (gradeSuppressStateUrlUpdate) {
                    return;
                }
                updateUrlWithTableState(gradeTable, '', gradeCols.indexToName, 50);
            });
            gradeTable.one('xhr.dt', function(e, settings, json) {
                if (json && typeof json.recordsFiltered === 'number'
                    && gradeInitialState.displayStart >= json.recordsFiltered
                    && json.recordsFiltered > 0) {
                    gradeSuppressStateUrlUpdate = false;
                    gradeTable.page('first').draw('page');
                }
            });

            // Handle filter form submission
            $('#absence-filter-form').on('submit', function(e) {
                e.preventDefault();

                gradeFilterData.start_date = $('#start_date').val() || '';
                gradeFilterData.end_date = $('#end_date').val() || '';
                gradeFilterData.counties = getCheckedValues('counties[]');
                gradeFilterData.regions = getCheckedValues('regions[]');
                gradeFilterData.in_game_network = $('#in-game-network').is(':checked') ? '1' : '';

                // Update URL with filter parameters
                updateUrlWithFilters(gradeFilterData);

                // Reset pagination in URL (but preserve per_page and sort_*).
                var filterUrl = new URL(window.location.href);
                filterUrl.searchParams.delete('page_num');
                window.history.replaceState({}, '', filterUrl.toString());

                // Update filter badge and Clear All Filters button
                updateFilterUI(gradeFilterData);

                // Close offcanvas
                var offcanvasEl = document.getElementById('offcanvasFilters');
                if (offcanvasEl) {
                    var offcanvas = bootstrap.Offcanvas.getInstance(offcanvasEl);
                    if (offcanvas) offcanvas.hide();
                }

                // Reload table and reset paging to page 1
                gradeTable.ajax.reload(null, true);

                // Reload chart with updated filters
                reloadChart();
            });

        } catch (e) {
            //console.error('Error initializing Grade DataTable:', e);
        }
    }

    /**
     * Render trend change for grade table (includes grade-level chart URL)
     */
    function renderTrendChangeGrade(data, type, row) {
        if (data === null || data === undefined) {
            if (type === 'display') return '<span class="text-muted">-</span>';
            return 0;
        }

        if (type === 'display') {
            var colorClass, iconClass;
            if (data > 0) {
                colorClass = 'text-danger';
                iconClass = 'bi-arrow-up';
            } else if (data < 0) {
                colorClass = 'text-success';
                iconClass = 'bi-arrow-down';
            } else {
                colorClass = 'text-info';
                iconClass = 'bi-arrow-right';
            }

            var displayValue = (data > 0 ? '+' : '') + data + '%';

            // Include popover attributes for chart if chart_url is available
            if (row.chart_url) {
                var escapedChartUrl = row.chart_url
                    .replace(/&/g, '&amp;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#39;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;');

                return '<span class="' + colorClass + ' trend-change-popover d-inline-block text-decoration-none" ' +
                    'data-bs-toggle="popover" ' +
                    'data-bs-placement="left" ' +
                    'data-bs-custom-class="chart-popover" ' +
                    'data-bs-html="true" ' +
                    'data-chart-target="#absence-chart-embed-' + row.irn + '-' + row.grade + '" ' +
                    'data-chart-url="' + escapedChartUrl + '">' +
                    '<i class="bi ' + iconClass + '"></i> ' + displayValue +
                    '</span>';
            }

            return '<span class="' + colorClass + '"><i class="bi ' + iconClass + '"></i> ' + displayValue + '</span>';
        }

        if (type === 'export') {
            return data;
        }

        return data;
    }

    /**
     * Initialize Aggregator Tables (server-side processing for district tables)
     */
    function initAggregatorTables() {
        //console.log('Checking for Aggregator tables...');
        //console.log('Summary table found:', $('#cohort-summary-table').length > 0);
        //console.log('District tables found:', $('.cohort-district-datatable').length);

        // Initialize cohort summary table (client-side - small data set)
        var $summaryTable = $('#cohort-summary-table');
        if ($summaryTable.length > 0) {
            //console.log('Initializing cohort summary table...');
            try {
                if (!$.fn.dataTable.isDataTable($summaryTable)) {
                    var summaryDT = $summaryTable.DataTable({
                        paging: false,
                        searching: false,
                        info: false,
                        ordering: true,
                        order: [[0, 'asc']],
                        //dom: 'Brt',
                        dom: '<"row"<"col-6"B><"col-6"f>>rtip',
                        buttons: [
                            {
                                extend: 'csv',
                                text: '<i class="bi bi-filetype-csv"></i> CSV',
                                className: 'btn btn-primary btn-sm',
                                title: 'Cohort Summary'
                            },
                            {
                                extend: 'excel',
                                text: '<i class="bi bi-file-earmark-spreadsheet"></i> Excel',
                                className: 'btn btn-primary btn-sm',
                                title: 'Cohort Summary'
                            }
                        ],
                        drawCallback: function(settings) {
                            labelSortButtons(settings.nTable);
                        }
                    });
                    aggregatorTables.push(summaryDT);
                    //console.log('Cohort summary table initialized');
                }
            } catch (e) {
                //console.error('Error initializing cohort summary table:', e);
            }
        }

        // Initialize cohort district tables (server-side processing)
        $('.cohort-district-datatable').each(function() {
            var $table = $(this);
            var tableId = $table.attr('id') || 'unknown';
            //console.log('Found cohort district table:', tableId);

            try {
                if (!$.fn.dataTable.isDataTable($table)) {
                    var cohortName = $table.data('cohort-name') || 'District Data';
                    var districtIrns = $table.data('district-irns') || [];
                    var startDate = $table.data('start-date') || '';
                    var endDate = $table.data('end-date') || '';

                    //console.log('District IRNs for table:', districtIrns.length);

                    // Build a per-table URL param prefix so multiple cohorts on one
                    // page persist their state independently. The counter suffix
                    // (extracted from the static table ID, e.g. "cohort-district-table-2")
                    // disambiguates cohorts with slug-colliding names.
                    var cohortSlug = slugify(cohortName);
                    var counterMatch = /(\d+)$/.exec(tableId);
                    var counterSuffix = counterMatch ? counterMatch[1] : '0';
                    var cohortPrefix = 'cohort_' + cohortSlug + '_' + counterSuffix + '_';
                    var cohortCols = buildColumnMap(absenceTablesData.columnMaps && absenceTablesData.columnMaps.cohort);
                    var cohortInitialState = getTableStateFromUrl(cohortPrefix, cohortCols.nameToIndex, {
                        pageLength: 20,
                        sortCol: 0,
                        sortDir: 'asc'
                    });
                    var cohortSuppressStateUrlUpdate = true;

                    var districtDT = $table.DataTable({
                        processing: true,
                        serverSide: true,
                        ajax: {
                            url: absenceTablesData.ajaxurl,
                            type: 'POST',
                            data: function(d) {
                                d.action = 'cohort_district_table_data';
                                d.nonce = absenceTablesData.nonce;
                                d.district_irns = districtIrns;
                                d.start_date = startDate;
                                d.end_date = endDate;
                                return d;
                            },
                            dataSrc: function(json) {
                                //console.log('Cohort district AJAX response:', json);
                                if (json.error) {
                                    //console.error('Server error:', json.error);
                                }
                                return json.data || [];
                            },
                            error: function(xhr, error, thrown) {
                                //console.error('Cohort district DataTables AJAX error:', error, thrown);
                            }
                        },
                        displayStart: cohortInitialState.displayStart,
                        pageLength: cohortInitialState.pageLength,
                        lengthMenu: [[10, 20, 50, 100], [10, 20, 50, 100]],
                        order: cohortInitialState.order,
                        columns: [
                            {
                                data: 'name',
                                render: function(data, type, row) {
                                    if (type === 'display') {
                                        if (row.building_url) {
                                            var title = row.is_exception
                                                ? data + ' — ' + absenceTablesData.i18n.noDataAvailable
                                                : absenceTablesData.i18n.clickToViewPrefix + ' ' + data;
                                            return '<a class="district-link" href="' + row.building_url + '" title="' + title + '">' + data + '</a>';
                                        }
                                    }
                                    return data || '';
                                }
                            },
                            { data: 'county', defaultContent: '' },
                            {
                                data: 'enrollment',
                                className: 'text-end',
                                render: function(data, type) {
                                    if (data === null || data === undefined) {
                                        if (type === 'display') return '<span class="text-muted">-</span>';
                                        return -1;
                                    }
                                    if (type === 'display' && data) {
                                        if (data >= 1 && data < 10) {
                                            return '<em>&lt;10</em>';
                                        }
                                        return Number(data).toLocaleString();
                                    }
                                    return data || 0;
                                }
                            },
                            {
                                data: 'chronic_pct',
                                className: 'text-end',
                                render: function(data, type) {
                                    if (data === null || data === undefined) {
                                        if (type === 'display') return '<span class="text-muted">-</span>';
                                        return 0;
                                    }
                                    if (type === 'display') {
                                        return '<strong>' + data + '%</strong>';
                                    }
                                    return data;
                                }
                            },
                            {
                                data: 'change',
                                className: 'text-end',
                                render: renderTrendChange
                            },
                            {
                                data: 'satisfactory',
                                className: 'text-end',
                                render: renderPercentage
                            },
                            {
                                data: 'satisfactory',
                                className: 'text-end',
                                render: renderCount
                            },
                            {
                                data: 'at_risk',
                                className: 'text-end',
                                render: renderPercentage
                            },
                            {
                                data: 'at_risk',
                                className: 'text-end',
                                render: renderCount
                            },
                            {
                                data: 'moderate',
                                className: 'text-end',
                                render: renderPercentage
                            },
                            {
                                data: 'moderate',
                                className: 'text-end',
                                render: renderCount
                            },
                            {
                                data: 'severe',
                                className: 'text-end',
                                render: renderPercentage
                            },
                            {
                                data: 'severe',
                                className: 'text-end',
                                render: renderCount
                            },
                            {
                                data: 'superintendent',
                                orderable: false,
                                defaultContent: ''
                            }
                        ],
                        dom: '<"row"<"col-6"B><"col-6"f>>rtip',
                        buttons: [
                            {
                                text: '<i class="bi bi-filetype-csv"></i> CSV',
                                className: 'btn btn-primary btn-sm',
                                action: function(e, dt, node, config) {
                                    var $btn = $(node);
                                    var originalText = $btn.html();
                                    $btn.prop('disabled', true).html('<i class="bi bi-hourglass-split"></i> Exporting...');

                                    exportCohortTableToCSV($table, cohortName).finally(function() {
                                        $btn.prop('disabled', false).html(originalText);
                                    });
                                }
                            },
                            {
                                text: '<i class="bi bi-file-earmark-spreadsheet"></i> Excel',
                                className: 'btn btn-primary btn-sm',
                                action: function(e, dt, node, config) {
                                    var $btn = $(node);
                                    var originalText = $btn.html();
                                    $btn.prop('disabled', true).html('<i class="bi bi-hourglass-split"></i> Exporting...');

                                    exportCohortTableToExcel($table, cohortName).finally(function() {
                                        $btn.prop('disabled', false).html(originalText);
                                    });
                                }
                            }
                        ],
                        language: {
                            processing: '<div class="d-flex flex-column align-items-center"><div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div><p class="mt-2 mb-0 text-muted">Loading data...</p></div>',
                            emptyTable: 'No districts in this cohort.',
                            info: 'Showing _START_ to _END_ of _TOTAL_ districts',
                            infoEmpty: 'Showing 0 to 0 of 0 districts',
                            lengthMenu: 'Show _MENU_ districts',
                            search: 'Search:',
                            zeroRecords: 'No matching districts found'
                        },
                        drawCallback: function(settings) {
                            initTooltips();
                            initTrendPopovers();
                            labelSortButtons(settings.nTable);
                            // Announce table updates to screen readers
                            if (typeof announceToScreenReader === 'function') {
                                announceToScreenReader('Cohort table updated. Showing ' + settings._iRecordsDisplay + ' districts.');
                            }
                        }
                    });
                    aggregatorTables.push(districtDT);
                    //console.log('District table initialized with server-side processing:', tableId);

                    // Persist URL state after initial draw. Closures over cohortPrefix,
                    // cohortCols, cohortInitialState and cohortSuppressStateUrlUpdate
                    // keep per-table state isolated inside this .each() iteration.
                    (function(dt, prefix, cols, initialState) {
                        dt.one('init.dt', function() {
                            cohortSuppressStateUrlUpdate = false;
                        });
                        dt.on('page.dt length.dt order.dt', function() {
                            if (cohortSuppressStateUrlUpdate) {
                                return;
                            }
                            updateUrlWithTableState(dt, prefix, cols.indexToName, 20);
                        });
                        dt.one('xhr.dt', function(e, settings, json) {
                            if (json && typeof json.recordsFiltered === 'number'
                                && initialState.displayStart >= json.recordsFiltered
                                && json.recordsFiltered > 0) {
                                cohortSuppressStateUrlUpdate = false;
                                dt.page('first').draw('page');
                            }
                        });
                    })(districtDT, cohortPrefix, cohortCols, cohortInitialState);
                }
            } catch (e) {
                //console.error('Error initializing district table ' + tableId + ':', e);
            }
        });

        //console.log('Total aggregator tables initialized:', aggregatorTables.length);
    }

    /**
     * Render count with percentage
     */
    function renderCountWithPercent(data, type, row) {
        if (!data) return '';

        if (type === 'display') {
            var html = data.display || String(data.count || 0);
            if (data.pct !== null && data.pct !== undefined) {
                html += ' <span class="text-muted">(' + data.pct + '%)</span>';
            }
            return html;
        }

        if (type === 'export') {
            return data.count || 0;
        }

        return data.count || 0;
    }

    /**
     * Render percentage value only
     */
    function renderPercentage(data, type) {
        if (!data) {
            if (type === 'display') return '<span class="text-muted">-</span>';
            return 0;
        }

        var pct = data.pct;
        if (pct === null || pct === undefined) {
            if (type === 'display') return '<span class="text-muted">-</span>';
            return 0;
        }

        if (type === 'display') {
            return pct + '%';
        }

        return pct;
    }

    /**
     * Render count value with privacy suppression
     */
    function renderCount(data, type) {
        if (!data) {
            if (type === 'display') return '<span class="text-muted">-</span>';
            return 0;
        }

        var count = data.count;
        if (count === null || count === undefined) {
            if (type === 'display') return '<span class="text-muted">-</span>';
            return 0;
        }

        if (type === 'display') {
            // Privacy suppression: show <10 for counts 1-9
            if (count >= 1 && count < 10) {
                return '<em>&lt;10</em>';
            }
            return Number(count).toLocaleString();
        }

        return count;
    }

    /**
     * Render trend change with color, icon, and popover for chart
     */
    function renderTrendChange(data, type, row) {
        if (data === null || data === undefined) {
            if (type === 'display') return '<span class="text-muted">-</span>';
            return 0;
        }

        if (type === 'display') {
            var colorClass, iconClass;
            if (data > 0) {
                colorClass = 'text-danger';
                iconClass = 'bi-arrow-up';
            } else if (data < 0) {
                colorClass = 'text-success';
                iconClass = 'bi-arrow-down';
            } else {
                colorClass = 'text-info';
                iconClass = 'bi-arrow-right';
            }

            var displayValue = (data > 0 ? '+' : '') + data + '%';

            // Include popover attributes for chart if chart_url is available
            if (row.chart_url) {
                // Escape the chart URL for use in HTML attribute
                var escapedChartUrl = row.chart_url
                    .replace(/&/g, '&amp;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#39;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;');

                return '<span class="' + colorClass + ' trend-change-popover d-inline-block text-decoration-none" ' +
                    'data-bs-toggle="popover" ' +
                    'data-bs-placement="left" ' +
                    'data-bs-custom-class="chart-popover" ' +
                    'data-bs-html="true" ' +
                    'data-chart-target="#absence-chart-embed-' + row.irn + '" ' +
                    'data-chart-url="' + escapedChartUrl + '">' +
                    '<i class="bi ' + iconClass + '"></i> ' + displayValue +
                    '</span>';
            }

            return '<span class="' + colorClass + '"><i class="bi ' + iconClass + '"></i> ' + displayValue + '</span>';
        }

        if (type === 'export') {
            return data;
        }

        return data;
    }

    /**
     * Update filter UI elements (badge count and Clear All Filters button)
     * Called after AJAX filter submission to reflect active filter state
     */
    function updateFilterUI(filterData) {
        var count = 0;
        if (filterData.start_date) count++;
        if (filterData.end_date) count++;
        if (filterData.counties && filterData.counties.length > 0) count++;
        if (filterData.regions && filterData.regions.length > 0) count++;
        if (filterData.in_game_network) count++;

        var $badge = $('.active-count');
        var $clearBtn = $('.clear-all-filters');

        if (count > 0) {
            $badge.text(count).removeClass('d-none');
            $clearBtn.removeClass('d-none');
        } else {
            $badge.addClass('d-none');
            $clearBtn.addClass('d-none');
        }
    }

    /**
     * Get checked checkbox values by name
     */
    function getCheckedValues(name) {
        var values = [];
        $('input[name="' + name + '"]:checked').each(function() {
            values.push($(this).val());
        });
        return values;
    }

    /**
     * Build forward and reverse column index maps from the PHP-provided rawMap.
     *
     * @param {Object} rawMap { "0": "name", "1": "county", ..., "13": null }
     * @returns {Object} { indexToName, nameToIndex }
     */
    function buildColumnMap(rawMap) {
        var indexToName = {};
        var nameToIndex = {};
        if (!rawMap) {
            return { indexToName: indexToName, nameToIndex: nameToIndex };
        }
        Object.keys(rawMap).forEach(function(k) {
            var idx = parseInt(k, 10);
            var name = rawMap[k];
            indexToName[idx] = name;
            if (name) {
                nameToIndex[name] = idx;
            }
        });
        return { indexToName: indexToName, nameToIndex: nameToIndex };
    }

    /**
     * Slugify a cohort name for use in URL param prefixes.
     */
    function slugify(str) {
        return String(str).toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
            .substring(0, 40);
    }

    /**
     * Read DataTables state (page, per-page, sort) from the current URL.
     *
     * @param {string} prefix    '' for main tables, 'cohort_<slug>_<n>_' for cohort tables
     * @param {Object} nameToIdx Reverse column map: { sort_by_value: columnIndex }
     * @param {Object} defaults  { pageLength: number, sortCol: number, sortDir: 'asc'|'desc' }
     * @returns {Object} { displayStart, pageLength, order } — ready for DataTables config
     */
    function getTableStateFromUrl(prefix, nameToIdx, defaults) {
        var params = new URLSearchParams(window.location.search);
        var pageNum = parseInt(params.get(prefix + 'page_num'), 10);
        var perPage = parseInt(params.get(prefix + 'per_page'), 10);
        var sortBy = params.get(prefix + 'sort_by');
        var sortDir = (params.get(prefix + 'sort_order') || '').toLowerCase();

        // Validate per_page against the lengthMenu values to prevent DoS via large values
        var validLengths = [10, 20, 50, 100];
        if (validLengths.indexOf(perPage) === -1) {
            perPage = defaults.pageLength;
        }

        // Validate and clamp page number
        if (isNaN(pageNum) || pageNum < 1) {
            pageNum = 1;
        }

        // Resolve sort_by -> column index; fall back to default if unknown/stale
        var sortCol = defaults.sortCol;
        if (sortBy && nameToIdx.hasOwnProperty(sortBy)) {
            sortCol = nameToIdx[sortBy];
        }
        var dir = (sortDir === 'desc') ? 'desc' : (sortDir === 'asc' ? 'asc' : defaults.sortDir);

        return {
            displayStart: Math.max(0, (pageNum - 1) * perPage),
            pageLength: perPage,
            order: [[sortCol, dir]]
        };
    }

    /**
     * Persist a DataTable's current pagination and sort state into the URL
     * via history.replaceState (not pushState — avoids filling history with
     * paginate clicks). Default values are omitted from the URL to keep it clean.
     *
     * @param {DataTable} dt                DataTables API instance
     * @param {string}    prefix            '' or 'cohort_<slug>_<n>_'
     * @param {Object}    idxToName         Forward column map: { columnIndex: sort_by_value }
     * @param {number}    defaultPageLength The table's default per_page (20 or 50)
     */
    function updateUrlWithTableState(dt, prefix, idxToName, defaultPageLength) {
        var info = dt.page.info();
        var order = dt.order();
        var pageNum = info.page + 1; // DataTables is 0-indexed
        var pageLen = info.length;
        var sortIdx = (order && order.length) ? order[0][0] : null;
        var sortDir = (order && order.length) ? order[0][1] : null;
        var sortName = (sortIdx !== null && idxToName[sortIdx]) ? idxToName[sortIdx] : null;

        var url = new URL(window.location.href);
        var params = url.searchParams;

        // Always delete first, then add non-default values back
        params.delete(prefix + 'page_num');
        params.delete(prefix + 'per_page');
        params.delete(prefix + 'sort_by');
        params.delete(prefix + 'sort_order');

        if (pageNum > 1) {
            params.set(prefix + 'page_num', String(pageNum));
        }
        if (pageLen && pageLen !== defaultPageLength) {
            params.set(prefix + 'per_page', String(pageLen));
        }
        if (sortName) {
            params.set(prefix + 'sort_by', sortName);
        }
        if (sortDir) {
            params.set(prefix + 'sort_order', sortDir.toUpperCase());
        }

        window.history.replaceState({}, '', url.toString());
    }

    /**
     * Update URL with filter parameters
     * This ensures chart popovers can read filters and allows bookmarking filtered views
     */
    function updateUrlWithFilters(filterData) {
        var url = new URL(window.location.href);
        var params = url.searchParams;

        // Clear existing filter params
        params.delete('start_date');
        params.delete('end_date');
        params.delete('counties[]');
        params.delete('regions[]');
        params.delete('in_game_network');

        // Add new filter params (only if they have values)
        if (filterData.start_date) {
            params.set('start_date', filterData.start_date);
        }
        if (filterData.end_date) {
            params.set('end_date', filterData.end_date);
        }
        if (filterData.counties && filterData.counties.length > 0) {
            filterData.counties.forEach(function(county) {
                params.append('counties[]', county);
            });
        }
        if (filterData.regions && filterData.regions.length > 0) {
            filterData.regions.forEach(function(region) {
                params.append('regions[]', region);
            });
        }
        if (filterData.in_game_network) {
            params.set('in_game_network', filterData.in_game_network);
        }

        // Update URL without page reload
        window.history.replaceState({}, '', url.toString());
    }

    /**
     * Reload the main chart with updated filter parameters
     * This reloads the chart container (e.g., #absence-chart-main) via AJAX
     */
    function reloadChart() {
        // District page uses #absence-chart-main; building/grade pages use
        // #absence-chart-<irn>. Fall back to the main chart container marked
        // with .datachart-styled[data-chart-url] so all page types are covered.
        var $chartContainer = $('#absence-chart-main');

        if ($chartContainer.length === 0) {
            $chartContainer = $('.datachart-styled[data-chart-url]').first();
        }

        if ($chartContainer.length === 0) {
            //console.log('No chart container found to reload');
            return;
        }

        var chartURL = $chartContainer.data('chart-url');

        if (!chartURL) {
            //console.log('No chart URL found');
            return;
        }

        // Show loading spinner
        $chartContainer.html(
            '<div class="text-center py-5">' +
                '<div class="spinner-border text-primary" role="status">' +
                    '<span class="visually-hidden">Loading...</span>' +
                '</div>' +
                '<p class="mt-2">Loading chart data...</p>' +
            '</div>'
        );

        // Get current URL parameters (filters) - these were just updated by updateUrlWithFilters
        var urlParams = new URLSearchParams(window.location.search);
        var ajaxData = { ajax: 1 };

        // Add filter parameters
        if (urlParams.has('start_date')) {
            ajaxData.start_date = urlParams.get('start_date');
        }
        if (urlParams.has('end_date')) {
            ajaxData.end_date = urlParams.get('end_date');
        }
        if (urlParams.has('in_game_network')) {
            ajaxData.in_game_network = urlParams.get('in_game_network');
        }

        // Handle multi-select arrays
        var counties = urlParams.getAll('counties[]');
        if (counties.length > 0) {
            ajaxData['counties[]'] = counties;
        }
        var regions = urlParams.getAll('regions[]');
        if (regions.length > 0) {
            ajaxData['regions[]'] = regions;
        }
        // Use jQuery AJAX to reload chart
        $.ajax({
            url: chartURL,
            data: ajaxData,
            type: 'GET',
            async: true,
            cache: false,
            success: function(response) {
                $chartContainer.html(response);

                // Initialize chart if echarts is available
                if (typeof echarts !== 'undefined') {
                    requestAnimationFrame(function() {
                        // Find the chart element within this container
                        var chartElement = $chartContainer.find('[id^="absence-chart-embed"]')[0];
                        if (chartElement) {
                            var chart = echarts.init(chartElement);
                            chart.resize();

                            // Add to tracked instances for resize handling
                            if (typeof chartInstances !== 'undefined') {
                                chartInstances.push(chart);
                            }
                        }
                    });
                }
            },
            error: function(xhr, status, error) {
                $chartContainer.html(
                    '<div class="alert alert-danger" role="alert">' +
                        'Failed to load chart data. Please try again later.' +
                    '</div>'
                );
                //console.error('Error loading chart:', error);
            }
        });
    }

    /**
     * Initialize Bootstrap tooltips
     */
    function initTooltips() {
        var tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        tooltipTriggerList.forEach(function(el) {
            new bootstrap.Tooltip(el);
        });
    }

    /**
     * Initialize trend change popovers for chart display
     * This is called after DataTables draws to initialize popovers on dynamically loaded content
     */
    function initTrendPopovers() {
        // Only initialize popovers that haven't been initialized yet
        var popoverTriggerList = document.querySelectorAll('.trend-change-popover:not([data-popover-initialized])');

        if (popoverTriggerList.length === 0) {
            return;
        }

        //console.log('Initializing', popoverTriggerList.length, 'trend change popovers');

        var popoverOptions = {
            trigger: 'manual',
            placement: 'left',
            sanitize: false,
            html: true,
            template: '<div class="popover shadow" role="tooltip"><div class="popover-arrow"></div><div class="popover-body"></div></div>',
            content: '<div class="d-flex justify-content-center align-items-center" style="width: 300px; height: 200px;"><div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div></div>'
        };

        popoverTriggerList.forEach(function(popoverTriggerEl) {
            // Mark as initialized
            popoverTriggerEl.setAttribute('data-popover-initialized', 'true');

            var popover = new bootstrap.Popover(popoverTriggerEl, popoverOptions);
            var timeout;
            var isPopoverOver = false;

            // Show popover on mouseenter
            popoverTriggerEl.addEventListener('mouseenter', function() {
                clearTimeout(timeout);
                popover.show();
            });

            // Handle mouseleave on trigger element
            popoverTriggerEl.addEventListener('mouseleave', function() {
                timeout = setTimeout(function() {
                    if (!isPopoverOver) {
                        popover.hide();
                    }
                }, 300);
            });

            // Add click/tap support for touch devices
            popoverTriggerEl.addEventListener('click', function(e) {
                e.preventDefault();
                if (this.getAttribute('aria-describedby')) {
                    popover.hide();
                } else {
                    popover.show();
                }
            });

            // Initialize chart when popover is shown
            popoverTriggerEl.addEventListener('shown.bs.popover', function() {
                var popoverId = this.getAttribute('aria-describedby');
                if (!popoverId) return;

                var popoverElement = document.getElementById(popoverId);
                if (!popoverElement) return;

                // Add mouseenter/mouseleave events to popover
                popoverElement.addEventListener('mouseenter', function() {
                    isPopoverOver = true;
                    clearTimeout(timeout);
                });

                popoverElement.addEventListener('mouseleave', function() {
                    isPopoverOver = false;
                    timeout = setTimeout(function() {
                        popover.hide();
                    }, 300);
                });

                // Create chart container
                var chartId = 'chart-' + popoverId;
                var chartContainer = document.createElement('div');
                chartContainer.id = chartId;
                chartContainer.className = 'trend-chart';
                chartContainer.style.width = '300px';
                chartContainer.style.height = '200px';

                // Get chart URL and target from the trigger element
                var chartURL = popoverTriggerEl.getAttribute('data-chart-url');
                var chartTargetId = popoverTriggerEl.getAttribute('data-chart-target');

                // Get popover body and append chart container
                var popoverBody = popoverElement.querySelector('.popover-body');
                popoverBody.innerHTML = '';
                popoverBody.appendChild(chartContainer);

                // Add loading indicator (matches absence-charts.js styling)
                jQuery(chartContainer).html(
                    '<div class="d-flex flex-column justify-content-center align-items-center" style="height: 200px;">' +
                        '<div class="spinner-border text-primary" role="status">' +
                            '<span class="visually-hidden">Loading...</span>' +
                        '</div>' +
                        '<p class="mt-2 mb-0 text-muted small">Loading chart data...</p>' +
                    '</div>'
                );

                if (chartURL) {
                    // Get current URL parameters (filters) to pass to chart
                    var urlParams = new URLSearchParams(window.location.search);
                    var ajaxData = { ajax: 1 };

                    // Get date filters from URL params, falling back to cookie if not in URL
                    var startDate = urlParams.get('start_date') || '';
                    var endDate = urlParams.get('end_date') || '';
                    var inGameNetwork = urlParams.get('in_game_network') || '';

                    // If dates not in URL, check the aggregator cookie
                    if (!startDate && !endDate) {
                        var cookieMatch = document.cookie.match(new RegExp('absent_aggregator=([^;]+)'));
                        if (cookieMatch) {
                            try {
                                var cookieData = JSON.parse(decodeURIComponent(cookieMatch[1]));
                                if (!Array.isArray(cookieData)) {
                                    startDate = cookieData.start_date || '';
                                    endDate = cookieData.end_date || '';
                                }
                            } catch (e) {
                                // Ignore cookie parse errors
                            }
                        }
                    }

                    // Add filter parameters
                    if (startDate) {
                        ajaxData.start_date = startDate;
                    }
                    if (endDate) {
                        ajaxData.end_date = endDate;
                    }
                    if (inGameNetwork) {
                        ajaxData.in_game_network = inGameNetwork;
                    }

                    // Handle multi-select arrays
                    var counties = urlParams.getAll('counties[]');
                    if (counties.length > 0) {
                        ajaxData['counties[]'] = counties;
                    }
                    var regions = urlParams.getAll('regions[]');
                    if (regions.length > 0) {
                        ajaxData['regions[]'] = regions;
                    }

                    // Use jQuery AJAX to load chart data (same approach as absence-charts.js)
                    jQuery.ajax({
                        url: chartURL,
                        data: ajaxData,
                        type: 'GET',
                        async: true,
                        cache: false,
                        success: function(response) {
                            jQuery(chartContainer).html(response);

                            // Initialize chart if echarts is available
                            if (typeof echarts !== 'undefined') {
                                requestAnimationFrame(function() {
                                    var chartElement = document.querySelector(chartTargetId);
                                    if (chartElement) {
                                        var chart = echarts.init(chartElement);
                                        chart.resize();
                                    }
                                });
                            }
                        },
                        error: function(xhr, status, error) {
                            jQuery(chartContainer).html(
                                '<div class="text-muted text-center p-3">Unable to load chart</div>'
                            );
                            //console.error('Error loading chart:', error);
                        }
                    });
                }
            });
        });
    }

    /**
     * Bind Export All button
     */
    function bindExportAllButton() {
        var $exportBtn = $('#export-all-excel');
        //console.log('Export All button found:', $exportBtn.length > 0);

        if ($exportBtn.length === 0) {
            //console.log('No Export All button on this page');
            return;
        }

        $exportBtn.on('click', function(e) {
            e.preventDefault();
            var $btn = $(this);

            //console.log('Export All clicked');
            //console.log('JSZip available:', typeof JSZip !== 'undefined');

            if (typeof JSZip === 'undefined') {
                //console.error('JSZip not loaded - cannot export to Excel');
                alert('Export library not loaded. Please refresh the page and try again.');
                return;
            }

            $btn.prop('disabled', true).html('<i class="bi bi-hourglass-split"></i> Exporting...');

            // Use async export function
            exportAllTablesToExcel()
                .then(function() {
                    $btn.prop('disabled', false).html('<i class="bi bi-file-earmark-spreadsheet"></i> Export All Tables to Excel');
                })
                .catch(function(err) {
                    //console.error('Export error:', err);
                    alert('Error exporting data: ' + err.message);
                    $btn.prop('disabled', false).html('<i class="bi bi-file-earmark-spreadsheet"></i> Export All Tables to Excel');
                });
        });
    }

    /**
     * Fetch all data for a cohort district table via AJAX
     * @param {jQuery} $table - The table element
     * @returns {Promise} - Promise resolving to array of row data
     */
    function fetchAllCohortDistrictData($table) {
        return new Promise(function(resolve, reject) {
            var districtIrns = $table.data('district-irns') || [];
            var startDate = $table.data('start-date') || '';
            var endDate = $table.data('end-date') || '';

            if (districtIrns.length === 0) {
                resolve([]);
                return;
            }

            $.ajax({
                url: absenceTablesData.ajaxurl,
                type: 'POST',
                data: {
                    action: 'cohort_district_table_data',
                    nonce: absenceTablesData.nonce,
                    district_irns: districtIrns,
                    start_date: startDate,
                    end_date: endDate,
                    export_all: 'true'
                },
                success: function(response) {
                    if (response && response.data) {
                        // Transform AJAX data to export format
                        var rows = response.data.map(function(item) {
                            // Format percentage
                            var formatPct = function(data) {
                                if (!data || data.pct === null || data.pct === undefined) return '-';
                                return data.pct + '%';
                            };

                            // Format count with privacy suppression
                            var formatCount = function(data) {
                                if (!data || data.count === null || data.count === undefined) return '-';
                                var count = data.count || 0;
                                if (count >= 1 && count < 10) return '<10';
                                return count.toLocaleString();
                            };

                            // Format trend change
                            var formatChange = function(val) {
                                if (val === null || val === undefined) return '-';
                                return (val > 0 ? '+' : '') + val + '%';
                            };

                            return [
                                item.name || '',
                                item.county || '',
                                (item.enrollment !== null && item.enrollment !== undefined) ? (item.enrollment >= 1 && item.enrollment < 10 ? '<10' : item.enrollment.toLocaleString()) : '-',
                                item.chronic_pct !== null ? item.chronic_pct + '%' : '-',
                                formatChange(item.change),
                                formatPct(item.satisfactory),
                                formatCount(item.satisfactory),
                                formatPct(item.at_risk),
                                formatCount(item.at_risk),
                                formatPct(item.moderate),
                                formatCount(item.moderate),
                                formatPct(item.severe),
                                formatCount(item.severe),
                                item.superintendent || ''
                            ];
                        });
                        resolve(rows);
                    } else {
                        resolve([]);
                    }
                },
                error: function(xhr, status, error) {
                    //console.error('Error fetching export data:', error);
                    reject(error);
                }
            });
        });
    }

    /**
     * Export a single cohort district table to CSV
     * Fetches all data via AJAX before exporting
     * @param {jQuery} $table - The table element
     * @param {string} cohortName - Name of the cohort for filename
     * @returns {Promise}
     */
    function exportCohortTableToCSV($table, cohortName) {
        return new Promise(function(resolve, reject) {
            // Get headers from the table
            var headers = [];
            $table.find('thead th').each(function() {
                headers.push($(this).text().trim());
            });

            // Fetch all data via AJAX
            fetchAllCohortDistrictData($table).then(function(rows) {
                //console.log('Exporting ' + rows.length + ' rows to CSV for: ' + cohortName);

                // Build CSV content
                var csvContent = '';

                // Add headers
                csvContent += headers.map(function(h) {
                    return '"' + h.replace(/"/g, '""') + '"';
                }).join(',') + '\n';

                // Add rows
                rows.forEach(function(row) {
                    csvContent += row.map(function(cell) {
                        return '"' + String(cell).replace(/"/g, '""') + '"';
                    }).join(',') + '\n';
                });

                // Create and download file
                var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                var filename = (cohortName + ' - Districts').replace(/[^a-z0-9]/gi, '_') + '.csv';

                // Create download link
                var link = document.createElement('a');
                if (navigator.msSaveBlob) {
                    // IE 10+
                    navigator.msSaveBlob(blob, filename);
                } else {
                    link.href = URL.createObjectURL(blob);
                    link.download = filename;
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                }

                resolve();
            }).catch(function(error) {
                //console.error('Error exporting to CSV:', error);
                alert('Error exporting data. Please try again.');
                reject(error);
            });
        });
    }

    /**
     * Export a single cohort district table to Excel
     * Fetches all data via AJAX before exporting
     * @param {jQuery} $table - The table element
     * @param {string} cohortName - Name of the cohort for filename
     * @returns {Promise}
     */
    function exportCohortTableToExcel($table, cohortName) {
        return new Promise(function(resolve, reject) {
            if (typeof JSZip === 'undefined') {
                alert('Export library not loaded. Please refresh the page and try again.');
                reject(new Error('JSZip not loaded'));
                return;
            }

            // Get headers from the table
            var headers = [];
            $table.find('thead th').each(function() {
                headers.push($(this).text().trim());
            });

            // Fetch all data via AJAX
            fetchAllCohortDistrictData($table).then(function(rows) {
                //console.log('Exporting ' + rows.length + ' rows to Excel for: ' + cohortName);

                // Create workbook with single sheet
                var sheetName = cohortName.substring(0, 31).replace(/[\\\/\*\?\[\]:]/g, '-');
                var tables = [{
                    name: sheetName,
                    headers: headers,
                    rows: rows
                }];

                var wb = createWorkbook(tables);
                var filename = (cohortName + ' - Districts').replace(/[^a-z0-9]/gi, '_') + '.xlsx';
                generateAndDownloadXLSX(wb, filename);

                resolve();
            }).catch(function(error) {
                //console.error('Error exporting to Excel:', error);
                alert('Error exporting data. Please try again.');
                reject(error);
            });
        });
    }

    /**
     * Fetch all data for Basic Search table via AJAX
     * @returns {Promise} - Promise resolving to array of row data
     */
    function fetchAllBasicSearchData() {
        return new Promise(function(resolve, reject) {
            $.ajax({
                url: absenceTablesData.ajaxurl,
                type: 'POST',
                data: {
                    action: 'absence_table_data',
                    nonce: absenceTablesData.nonce,
                    type: filterData.type,
                    year: filterData.year,
                    start_date: filterData.start_date,
                    end_date: filterData.end_date,
                    counties: filterData.counties,
                    regions: filterData.regions,
                    in_game_network: filterData.in_game_network,
                    export_all: 'true'
                },
                success: function(response) {
                    if (response && response.data) {
                        // Transform AJAX data to export format
                        var rows = response.data.map(function(item) {
                            // Format percentage
                            var formatPct = function(data) {
                                if (!data || data.pct === null || data.pct === undefined) return '-';
                                return data.pct + '%';
                            };

                            // Format count with privacy suppression
                            var formatCount = function(data) {
                                if (!data || data.count === null || data.count === undefined) return '-';
                                var count = data.count || 0;
                                if (count >= 1 && count < 10) return '<10';
                                return count.toLocaleString();
                            };

                            // Format trend change
                            var formatChange = function(val) {
                                if (val === null || val === undefined) return '-';
                                return (val > 0 ? '+' : '') + val + '%';
                            };

                            return [
                                item.name || '',
                                item.county || '',
                                (item.enrollment !== null && item.enrollment !== undefined) ? (item.enrollment >= 1 && item.enrollment < 10 ? '<10' : item.enrollment.toLocaleString()) : '-',
                                (item.chronic_pct !== null && item.chronic_pct !== undefined) ? item.chronic_pct + '%' : '-',
                                formatChange(item.change),
                                formatPct(item.satisfactory),
                                formatCount(item.satisfactory),
                                formatPct(item.at_risk),
                                formatCount(item.at_risk),
                                formatPct(item.moderate),
                                formatCount(item.moderate),
                                formatPct(item.severe),
                                formatCount(item.severe),
                                item.superintendent || ''
                            ];
                        });
                        resolve(rows);
                    } else {
                        resolve([]);
                    }
                },
                error: function(xhr, status, error) {
                    //console.error('Error fetching Basic Search export data:', error);
                    reject(error);
                }
            });
        });
    }

    /**
     * Export Basic Search table to CSV
     * Fetches all data via AJAX before exporting
     * @returns {Promise}
     */
    function exportBasicSearchToCSV() {
        return new Promise(function(resolve, reject) {
            var headers = [
                'District Name',
                'County',
                'Enrollment',
                'Chronic %',
                'Change',
                'Satisfactory %',
                'Satisfactory #',
                'At Risk %',
                'At Risk #',
                'Moderate %',
                'Moderate #',
                'Severe %',
                'Severe #',
                'Superintendent'
            ];

            // Fetch all data via AJAX
            fetchAllBasicSearchData().then(function(rows) {
                //console.log('Exporting ' + rows.length + ' rows to CSV for Basic Search');

                // Build CSV content
                var csvContent = '';

                // Add headers
                csvContent += headers.map(function(h) {
                    return '"' + h.replace(/"/g, '""') + '"';
                }).join(',') + '\n';

                // Add rows
                rows.forEach(function(row) {
                    csvContent += row.map(function(cell) {
                        return '"' + String(cell).replace(/"/g, '""') + '"';
                    }).join(',') + '\n';
                });

                // Create and download file
                var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                var filename = 'District_Absence_Data.csv';

                // Create download link
                var link = document.createElement('a');
                if (navigator.msSaveBlob) {
                    navigator.msSaveBlob(blob, filename);
                } else {
                    link.href = URL.createObjectURL(blob);
                    link.download = filename;
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                }

                resolve();
            }).catch(function(error) {
                //console.error('Error exporting Basic Search to CSV:', error);
                alert('Error exporting data. Please try again.');
                reject(error);
            });
        });
    }

    /**
     * Export Basic Search table to Excel
     * Fetches all data via AJAX before exporting
     * @returns {Promise}
     */
    function exportBasicSearchToExcel() {
        return new Promise(function(resolve, reject) {
            if (typeof JSZip === 'undefined') {
                alert('Export library not loaded. Please refresh the page and try again.');
                reject(new Error('JSZip not loaded'));
                return;
            }

            var headers = [
                'District Name',
                'County',
                'Enrollment',
                'Chronic %',
                'Change',
                'Satisfactory %',
                'Satisfactory #',
                'At Risk %',
                'At Risk #',
                'Moderate %',
                'Moderate #',
                'Severe %',
                'Severe #',
                'Superintendent'
            ];

            // Fetch all data via AJAX
            fetchAllBasicSearchData().then(function(rows) {
                //console.log('Exporting ' + rows.length + ' rows to Excel for Basic Search');

                // Create workbook with single sheet
                var tables = [{
                    name: 'District Absence Data',
                    headers: headers,
                    rows: rows
                }];

                var wb = createWorkbook(tables);
                generateAndDownloadXLSX(wb, 'District_Absence_Data.xlsx');

                resolve();
            }).catch(function(error) {
                //console.error('Error exporting Basic Search to Excel:', error);
                alert('Error exporting data. Please try again.');
                reject(error);
            });
        });
    }

    /**
     * Fetch all data for Building table via AJAX
     * @returns {Promise} - Promise resolving to array of row data
     */
    function fetchAllBuildingData() {
        return new Promise(function(resolve, reject) {
            $.ajax({
                url: absenceTablesData.ajaxurl,
                type: 'POST',
                data: {
                    action: 'building_table_data',
                    nonce: absenceTablesData.nonce,
                    irn: buildingFilterData.irn,
                    start_date: buildingFilterData.start_date,
                    end_date: buildingFilterData.end_date,
                    counties: buildingFilterData.counties,
                    regions: buildingFilterData.regions,
                    in_game_network: buildingFilterData.in_game_network,
                    export_all: 'true'
                },
                success: function(response) {
                    if (response && response.data) {
                        // Transform AJAX data to export format
                        var rows = response.data.map(function(item) {
                            // Format percentage
                            var formatPct = function(data) {
                                if (!data || data.pct === null || data.pct === undefined) return '-';
                                return data.pct + '%';
                            };

                            // Format count with privacy suppression
                            var formatCount = function(data) {
                                if (!data || data.count === null || data.count === undefined) return '-';
                                var count = data.count || 0;
                                if (count >= 1 && count < 10) return '<10';
                                return count.toLocaleString();
                            };

                            // Format trend change
                            var formatChange = function(val) {
                                if (val === null || val === undefined) return '-';
                                return (val > 0 ? '+' : '') + val + '%';
                            };

                            return [
                                item.name || '',
                                item.county || '',
                                (item.enrollment !== null && item.enrollment !== undefined) ? (item.enrollment >= 1 && item.enrollment < 10 ? '<10' : item.enrollment.toLocaleString()) : '-',
                                (item.chronic_pct !== null && item.chronic_pct !== undefined) ? item.chronic_pct + '%' : '-',
                                formatChange(item.change),
                                formatPct(item.satisfactory),
                                formatCount(item.satisfactory),
                                formatPct(item.at_risk),
                                formatCount(item.at_risk),
                                formatPct(item.moderate),
                                formatCount(item.moderate),
                                formatPct(item.severe),
                                formatCount(item.severe),
                                item.principal || ''
                            ];
                        });
                        resolve(rows);
                    } else {
                        resolve([]);
                    }
                },
                error: function(xhr, status, error) {
                    //console.error('Error fetching Building export data:', error);
                    reject(error);
                }
            });
        });
    }

    /**
     * Export Building table to CSV
     * Fetches all data via AJAX before exporting
     * @returns {Promise}
     */
    function exportBuildingToCSV() {
        return new Promise(function(resolve, reject) {
            var headers = [
                'Building Name',
                'County',
                'Enrollment',
                'Chronic %',
                'Change',
                'Satisfactory %',
                'Satisfactory #',
                'At Risk %',
                'At Risk #',
                'Moderate %',
                'Moderate #',
                'Severe %',
                'Severe #',
                'Principal'
            ];

            // Fetch all data via AJAX
            fetchAllBuildingData().then(function(rows) {
                //console.log('Exporting ' + rows.length + ' rows to CSV for Building table');

                // Build CSV content
                var csvContent = '';

                // Add headers
                csvContent += headers.map(function(h) {
                    return '"' + h.replace(/"/g, '""') + '"';
                }).join(',') + '\n';

                // Add rows
                rows.forEach(function(row) {
                    csvContent += row.map(function(cell) {
                        return '"' + String(cell).replace(/"/g, '""') + '"';
                    }).join(',') + '\n';
                });

                // Create and download file
                var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                var filename = 'Building_Absence_Data.csv';

                // Create download link
                var link = document.createElement('a');
                if (navigator.msSaveBlob) {
                    navigator.msSaveBlob(blob, filename);
                } else {
                    link.href = URL.createObjectURL(blob);
                    link.download = filename;
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                }

                resolve();
            }).catch(function(error) {
                //console.error('Error exporting Building to CSV:', error);
                alert('Error exporting data. Please try again.');
                reject(error);
            });
        });
    }

    /**
     * Export Building table to Excel
     * Fetches all data via AJAX before exporting
     * @returns {Promise}
     */
    function exportBuildingToExcel() {
        return new Promise(function(resolve, reject) {
            if (typeof JSZip === 'undefined') {
                alert('Export library not loaded. Please refresh the page and try again.');
                reject(new Error('JSZip not loaded'));
                return;
            }

            var headers = [
                'Building Name',
                'County',
                'Enrollment',
                'Chronic %',
                'Change',
                'Satisfactory %',
                'Satisfactory #',
                'At Risk %',
                'At Risk #',
                'Moderate %',
                'Moderate #',
                'Severe %',
                'Severe #',
                'Principal'
            ];

            // Fetch all data via AJAX
            fetchAllBuildingData().then(function(rows) {
                //console.log('Exporting ' + rows.length + ' rows to Excel for Building table');

                // Create workbook with single sheet
                var tables = [{
                    name: 'Building Absence Data',
                    headers: headers,
                    rows: rows
                }];

                var wb = createWorkbook(tables);
                generateAndDownloadXLSX(wb, 'Building_Absence_Data.xlsx');

                resolve();
            }).catch(function(error) {
                //console.error('Error exporting Building to Excel:', error);
                alert('Error exporting data. Please try again.');
                reject(error);
            });
        });
    }

    /**
     * Fetch all data for Grade table via AJAX
     * @returns {Promise} - Promise resolving to array of row data
     */
    function fetchAllGradeData() {
        return new Promise(function(resolve, reject) {
            $.ajax({
                url: absenceTablesData.ajaxurl,
                type: 'POST',
                data: {
                    action: 'grade_table_data',
                    nonce: absenceTablesData.nonce,
                    irn: gradeFilterData.irn,
                    start_date: gradeFilterData.start_date,
                    end_date: gradeFilterData.end_date,
                    counties: gradeFilterData.counties,
                    regions: gradeFilterData.regions,
                    in_game_network: gradeFilterData.in_game_network,
                    export_all: 'true'
                },
                success: function(response) {
                    if (response && response.data) {
                        // Transform AJAX data to export format
                        var rows = response.data.map(function(item) {
                            // Format percentage
                            var formatPct = function(data) {
                                if (!data || data.pct === null || data.pct === undefined) return '-';
                                return data.pct + '%';
                            };

                            // Format count with privacy suppression
                            var formatCount = function(data) {
                                if (!data || data.count === null || data.count === undefined) return '-';
                                var count = data.count || 0;
                                if (count >= 1 && count < 10) return '<10';
                                return count.toLocaleString();
                            };

                            // Format trend change
                            var formatChange = function(val) {
                                if (val === null || val === undefined) return '-';
                                return (val > 0 ? '+' : '') + val + '%';
                            };

                            return [
                                item.grade || '',
                                (item.enrollment !== null && item.enrollment !== undefined) ? (item.enrollment >= 1 && item.enrollment < 10 ? '<10' : item.enrollment.toLocaleString()) : '-',
                                (item.chronic_pct !== null && item.chronic_pct !== undefined) ? item.chronic_pct + '%' : '-',
                                formatChange(item.change),
                                formatPct(item.satisfactory),
                                formatCount(item.satisfactory),
                                formatPct(item.at_risk),
                                formatCount(item.at_risk),
                                formatPct(item.moderate),
                                formatCount(item.moderate),
                                formatPct(item.severe),
                                formatCount(item.severe)
                            ];
                        });
                        resolve(rows);
                    } else {
                        resolve([]);
                    }
                },
                error: function(xhr, status, error) {
                    //console.error('Error fetching Grade export data:', error);
                    reject(error);
                }
            });
        });
    }

    /**
     * Export Grade table to CSV
     * Fetches all data via AJAX before exporting
     * @returns {Promise}
     */
    function exportGradeToCSV() {
        return new Promise(function(resolve, reject) {
            var headers = [
                'Grade',
                'Enrollment',
                'Chronic %',
                'Change',
                'Satisfactory %',
                'Satisfactory #',
                'At Risk %',
                'At Risk #',
                'Moderate %',
                'Moderate #',
                'Severe %',
                'Severe #'
            ];

            // Fetch all data via AJAX
            fetchAllGradeData().then(function(rows) {
                //console.log('Exporting ' + rows.length + ' rows to CSV for Grade table');

                // Build CSV content
                var csvContent = '';

                // Add headers
                csvContent += headers.map(function(h) {
                    return '"' + h.replace(/"/g, '""') + '"';
                }).join(',') + '\n';

                // Add rows
                rows.forEach(function(row) {
                    csvContent += row.map(function(cell) {
                        return '"' + String(cell).replace(/"/g, '""') + '"';
                    }).join(',') + '\n';
                });

                // Create and download file
                var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                var filename = 'Grade_Absence_Data.csv';

                // Create download link
                var link = document.createElement('a');
                if (navigator.msSaveBlob) {
                    navigator.msSaveBlob(blob, filename);
                } else {
                    link.href = URL.createObjectURL(blob);
                    link.download = filename;
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                }

                resolve();
            }).catch(function(error) {
                //console.error('Error exporting Grade to CSV:', error);
                alert('Error exporting data. Please try again.');
                reject(error);
            });
        });
    }

    /**
     * Export Grade table to Excel
     * Fetches all data via AJAX before exporting
     * @returns {Promise}
     */
    function exportGradeToExcel() {
        return new Promise(function(resolve, reject) {
            if (typeof JSZip === 'undefined') {
                alert('Export library not loaded. Please refresh the page and try again.');
                reject(new Error('JSZip not loaded'));
                return;
            }

            var headers = [
                'Grade',
                'Enrollment',
                'Chronic %',
                'Change',
                'Satisfactory %',
                'Satisfactory #',
                'At Risk %',
                'At Risk #',
                'Moderate %',
                'Moderate #',
                'Severe %',
                'Severe #'
            ];

            // Fetch all data via AJAX
            fetchAllGradeData().then(function(rows) {
                //console.log('Exporting ' + rows.length + ' rows to Excel for Grade table');

                // Create workbook with single sheet
                var tables = [{
                    name: 'Grade Absence Data',
                    headers: headers,
                    rows: rows
                }];

                var wb = createWorkbook(tables);
                generateAndDownloadXLSX(wb, 'Grade_Absence_Data.xlsx');

                resolve();
            }).catch(function(error) {
                //console.error('Error exporting Grade to Excel:', error);
                alert('Error exporting data. Please try again.');
                reject(error);
            });
        });
    }

    /**
     * Export all aggregator tables to Excel with multiple sheets
     * Fetches all data via AJAX for server-side processed tables
     */
    function exportAllTablesToExcel() {
        return new Promise(function(resolve, reject) {
            var tables = [];
            var promises = [];

            // Collect data from all aggregator tables
            $('.aggregator-table').each(function() {
                var $table = $(this);
                var sheetName = 'Summary';

                if ($table.data('cohort-name')) {
                    sheetName = $table.data('cohort-name');
                }

                // Sanitize sheet name (Excel limit: 31 chars, no special chars)
                sheetName = sheetName.substring(0, 31).replace(/[\\\/\*\?\[\]:]/g, '-');

                // Get headers from the table
                var headers = [];
                $table.find('thead th').each(function() {
                    headers.push($(this).text().trim());
                });

                // Check if this is a server-side processed cohort district table
                var isServerSide = $table.hasClass('cohort-district-datatable') && $table.data('district-irns');

                if (isServerSide) {
                    // Fetch ALL data via AJAX for server-side tables
                    var promise = fetchAllCohortDistrictData($table).then(function(rows) {
                        //console.log('Fetched ' + rows.length + ' rows via AJAX for: ' + sheetName);
                        tables.push({
                            name: sheetName,
                            headers: headers,
                            rows: rows
                        });
                    });
                    promises.push(promise);
                } else {
                    // For client-side tables (summary), read from DOM/DataTables
                    var rows = [];

                    if ($.fn.dataTable.isDataTable($table)) {
                        var dt = $table.DataTable();
                        dt.rows().every(function() {
                            var rowData = [];
                            var $row = $(this.node());
                            $row.find('td').each(function() {
                                rowData.push($(this).text().trim());
                            });
                            if (rowData.length > 0) {
                                rows.push(rowData);
                            }
                        });
                        //console.log('Exported ' + rows.length + ' rows from DataTable: ' + sheetName);
                    } else {
                        $table.find('tbody tr').each(function() {
                            var row = [];
                            $(this).find('td').each(function() {
                                row.push($(this).text().trim());
                            });
                            if (row.length > 0) {
                                rows.push(row);
                            }
                        });
                        //console.log('Exported ' + rows.length + ' rows from DOM: ' + sheetName);
                    }

                    tables.push({
                        name: sheetName,
                        headers: headers,
                        rows: rows
                    });
                }
            });

            // Wait for all AJAX requests to complete
            Promise.all(promises).then(function() {
                if (tables.length === 0) {
                    alert('No tables found to export.');
                    resolve();
                    return;
                }

                //console.log('Exporting', tables.length, 'tables to Excel');

                // Generate Excel file
                var wb = createWorkbook(tables);
                generateAndDownloadXLSX(wb, 'Cohort_Comparison_Data.xlsx');
                resolve();
            }).catch(function(error) {
                reject(error);
            });
        });
    }

    /**
     * Create workbook structure
     */
    function createWorkbook(tables) {
        var wb = {
            SheetNames: [],
            Sheets: {}
        };

        tables.forEach(function(table) {
            var sheetData = [table.headers].concat(table.rows);
            wb.SheetNames.push(table.name);
            wb.Sheets[table.name] = arrayToSheet(sheetData);
        });

        return wb;
    }

    /**
     * Convert array to sheet object
     */
    function arrayToSheet(data) {
        var sheet = {};
        var range = { s: { r: 0, c: 0 }, e: { r: data.length - 1, c: 0 } };

        for (var R = 0; R < data.length; ++R) {
            for (var C = 0; C < data[R].length; ++C) {
                if (range.e.c < C) range.e.c = C;

                var value = data[R][C];
                var cell = { v: value, t: 's' };

                // Try to detect numbers
                var cleanValue = String(value).replace(/,/g, '').trim();
                if (cleanValue !== '' && !isNaN(cleanValue)) {
                    cell.t = 'n';
                    cell.v = parseFloat(cleanValue);
                }

                var cellRef = cellAddress(C, R);
                sheet[cellRef] = cell;
            }
        }

        sheet['!ref'] = cellAddress(range.s.c, range.s.r) + ':' + cellAddress(range.e.c, range.e.r);
        return sheet;
    }

    /**
     * Convert column/row to Excel cell address
     */
    function cellAddress(c, r) {
        var col = '';
        var cc = c;
        while (cc >= 0) {
            col = String.fromCharCode((cc % 26) + 65) + col;
            cc = Math.floor(cc / 26) - 1;
        }
        return col + (r + 1);
    }

    /**
     * Generate and download XLSX file using JSZip
     */
    function generateAndDownloadXLSX(workbook, filename) {
        if (typeof JSZip === 'undefined') {
            //console.error('JSZip not loaded');
            alert('Export library not loaded. Please refresh the page and try again.');
            return;
        }

        var zip = new JSZip();

        // Build shared strings
        var sharedStrings = [];
        var sharedStringsMap = {};

        workbook.SheetNames.forEach(function(name) {
            var sheet = workbook.Sheets[name];
            for (var key in sheet) {
                if (key[0] === '!' || !sheet.hasOwnProperty(key)) continue;
                var cell = sheet[key];
                if (cell.t === 's') {
                    if (sharedStringsMap[cell.v] === undefined) {
                        sharedStringsMap[cell.v] = sharedStrings.length;
                        sharedStrings.push(cell.v);
                    }
                    cell.v = sharedStringsMap[cell.v];
                }
            }
        });

        // [Content_Types].xml
        var contentTypes = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
            '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
            '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
            '<Default Extension="xml" ContentType="application/xml"/>' +
            '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
            '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
            '<Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>';

        workbook.SheetNames.forEach(function(name, idx) {
            contentTypes += '<Override PartName="/xl/worksheets/sheet' + (idx + 1) + '.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>';
        });
        contentTypes += '</Types>';
        zip.file('[Content_Types].xml', contentTypes);

        // _rels/.rels
        zip.file('_rels/.rels', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
            '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
            '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
            '</Relationships>');

        // xl/_rels/workbook.xml.rels
        var wbRels = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
            '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
            '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
            '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/>';

        workbook.SheetNames.forEach(function(name, idx) {
            wbRels += '<Relationship Id="rId' + (idx + 3) + '" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet' + (idx + 1) + '.xml"/>';
        });
        wbRels += '</Relationships>';
        zip.file('xl/_rels/workbook.xml.rels', wbRels);

        // xl/workbook.xml
        var wbXml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
            '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
            '<sheets>';

        workbook.SheetNames.forEach(function(name, idx) {
            wbXml += '<sheet name="' + escapeXml(name) + '" sheetId="' + (idx + 1) + '" r:id="rId' + (idx + 3) + '"/>';
        });
        wbXml += '</sheets></workbook>';
        zip.file('xl/workbook.xml', wbXml);

        // xl/styles.xml
        zip.file('xl/styles.xml', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
            '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
            '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
            '<fills count="1"><fill><patternFill patternType="none"/></fill></fills>' +
            '<borders count="1"><border/></borders>' +
            '<cellStyleXfs count="1"><xf/></cellStyleXfs>' +
            '<cellXfs count="2"><xf/><xf fontId="1" applyFont="1"/></cellXfs>' +
            '</styleSheet>');

        // xl/sharedStrings.xml
        var ssXml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
            '<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="' + sharedStrings.length + '" uniqueCount="' + sharedStrings.length + '">';
        sharedStrings.forEach(function(str) {
            ssXml += '<si><t>' + escapeXml(String(str)) + '</t></si>';
        });
        ssXml += '</sst>';
        zip.file('xl/sharedStrings.xml', ssXml);

        // Worksheets
        workbook.SheetNames.forEach(function(name, idx) {
            var sheet = workbook.Sheets[name];
            var wsXml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
                '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
                '<sheetData>';

            var ref = sheet['!ref'] || 'A1:A1';
            var parts = ref.split(':');
            var startRef = parseCellRef(parts[0]);
            var endRef = parseCellRef(parts[1] || parts[0]);

            for (var R = startRef.r; R <= endRef.r; ++R) {
                wsXml += '<row r="' + (R + 1) + '">';
                for (var C = startRef.c; C <= endRef.c; ++C) {
                    var cr = cellAddress(C, R);
                    var cell = sheet[cr];
                    if (cell) {
                        var styleAttr = R === 0 ? ' s="1"' : '';
                        if (cell.t === 'n') {
                            wsXml += '<c r="' + cr + '"' + styleAttr + '><v>' + cell.v + '</v></c>';
                        } else {
                            wsXml += '<c r="' + cr + '" t="s"' + styleAttr + '><v>' + cell.v + '</v></c>';
                        }
                    }
                }
                wsXml += '</row>';
            }

            wsXml += '</sheetData></worksheet>';
            zip.file('xl/worksheets/sheet' + (idx + 1) + '.xml', wsXml);
        });

        // Generate and download
        zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
            .then(function(blob) {
                var url = URL.createObjectURL(blob);
                var a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                //console.log('Export complete');
            })
            .catch(function(err) {
                //console.error('Error generating Excel file:', err);
                alert('Error generating Excel file');
            });
    }

    /**
     * Parse cell reference
     */
    function parseCellRef(ref) {
        var col = 0;
        var row = 0;
        for (var i = 0; i < ref.length; i++) {
            var c = ref.charCodeAt(i);
            if (c >= 65 && c <= 90) {
                col = col * 26 + (c - 64);
            } else if (c >= 48 && c <= 57) {
                row = row * 10 + (c - 48);
            }
        }
        return { c: col - 1, r: row - 1 };
    }

    /**
     * Escape XML special characters
     */
    function escapeXml(str) {
        if (str === null || str === undefined) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
    }

    // Public API
    window.getAbsenceTable = function() { return absenceTable; };
    window.getBuildingTable = function() { return buildingTable; };
    window.getGradeTable = function() { return gradeTable; };
    window.getAggregatorTables = function() { return aggregatorTables; };

})(jQuery);
